import { withHomeState } from '../../../constants/location';
import { GSTIN_RX, PHONE_RX } from '../../../utils/validation';
import type { FormErrors } from '../../../hooks/useForm';
import type { Customer, CustomerInput, CustomerType } from '../types/customerTypes';

/** What the customer form edits (strings for inputs, paise for money). */
export interface CustomerFormValues {
  name: string;
  phone: string;
  altPhone: string;
  email: string;
  customerType: CustomerType;
  gstin: string;
  line: string;
  city: string;
  pincode: string;
  /** Paise, always positive; isAdvance flips the sign when saving */
  openingBalance: number | null;
  isAdvance: boolean;
  notes: string;
  isActive: boolean;
}

export const CUSTOMER_TYPES: { value: CustomerType; label: string }[] = [
  { value: 'individual', label: 'Individual / home owner' },
  { value: 'business', label: 'Business / builder' },
];

export const EMPTY_CUSTOMER: CustomerFormValues = {
  name: '', phone: '', altPhone: '', email: '', customerType: 'individual', gstin: '',
  line: '', city: '', pincode: '', openingBalance: null, isAdvance: false, notes: '', isActive: true,
};

/** Saved customer -> form values. */
export function fromCustomer(c?: Customer | null): CustomerFormValues {
  if (!c) return EMPTY_CUSTOMER;
  const a = c.billingAddress || {};
  return {
    ...EMPTY_CUSTOMER,
    name: c.name ?? '', phone: c.phone ?? '', altPhone: c.altPhone ?? '', email: c.email ?? '',
    customerType: c.customerType ?? 'individual', gstin: c.gstin ?? '',
    line: a.line ?? '', city: a.city ?? '', pincode: a.pincode ?? '',
    openingBalance: c.openingBalance ? Math.abs(c.openingBalance) : null,
    isAdvance: (c.openingBalance ?? 0) < 0,
    notes: c.notes ?? '', isActive: c.isActive ?? true,
  };
}

/** Returns { field: message } for every problem; empty when the form can be saved. */
export function validateCustomer(f: CustomerFormValues): FormErrors {
  const e: FormErrors = {};
  if (!f.name.trim()) e.name = 'Enter the customer name';
  const phone = f.phone.replace(/\s/g, '');
  if (phone && !PHONE_RX.test(phone)) e.phone = 'Enter a 10 digit mobile number';
  if (f.gstin && !GSTIN_RX.test(f.gstin.toUpperCase())) e.gstin = 'GSTIN should be 15 characters, like 27ABCDE1234F1Z5';
  if (f.pincode && !/^\d{6}$/.test(f.pincode)) e.pincode = 'Pincode should be 6 digits';
  if (f.email && !/^\S+@\S+\.\S+$/.test(f.email)) e.email = 'Enter a valid email';
  return e;
}

/** Form values -> API body. */
export function toCustomerInput(f: CustomerFormValues, editing: boolean): CustomerInput {
  return {
    name: f.name.trim(),
    phone: f.phone.replace(/\s/g, '') || undefined,
    altPhone: f.altPhone.trim() || undefined,
    email: f.email.trim(),
    customerType: f.customerType,
    gstin: f.gstin.trim().toUpperCase(),
    billingAddress: withHomeState({ line: f.line.trim(), city: f.city.trim(), pincode: f.pincode.trim() }),
    openingBalance: f.openingBalance ? (f.isAdvance ? -f.openingBalance : f.openingBalance) : 0,
    notes: f.notes.trim() || undefined,
    ...(editing ? { isActive: f.isActive } : {}),
  };
}

/** Backend error paths -> form field names */
export const CUSTOMER_SERVER_FIELDS: Record<string, string> = {
  'billingAddress.pincode': 'pincode',
  'billingAddress.line': 'line',
  'billingAddress.city': 'city',
};
